import { useState } from 'react'
import { motion } from 'framer-motion'
import ScrollReveal from '../shared/ScrollReveal'
import GlowButton from '../shared/GlowButton'
import { Icons } from '../shared/Icons'
import { COUNTRY_CODES } from '../../data/countryCodes'
import './CTASection.css'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xnjdpgkd'

export default function CTASection() {
  const [formData, setFormData] = useState({
    email: '',
    company: '',
    fleetSize: '',
    countryCode: '+1',
    phone: ''
  })
  const [status, setStatus] = useState('idle') // idle, submitting, success, error

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: `Demo Request from ${formData.company}`
        })
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          email: '',
          company: '',
          fleetSize: '',
          countryCode: '+1',
          phone: ''
        })
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <section className="cta-section">
      <div className="cta-bg">
        <div className="cta-glow cta-glow-1" />
        <div className="cta-glow cta-glow-2" />
      </div>

      <div className="container">
        <div className="cta-grid">
          <ScrollReveal className="cta-content">
            <h2 className="cta-title">
              Ready to see everything?
            </h2>
            <p className="cta-description">
              Start monitoring from land to space. Book a personalized demo
              and see how AI4Solar can transform your solar operations.
            </p>

            <div className="cta-features">
              <div className="cta-feature">
                <span className="cta-feature-icon">
                  <Icons.check size={16} />
                </span>
                <span>30-minute live demo</span>
              </div>
              <div className="cta-feature">
                <span className="cta-feature-icon">
                  <Icons.check size={16} />
                </span>
                <span>Custom to your fleet size</span>
              </div>
              <div className="cta-feature">
                <span className="cta-feature-icon">
                  <Icons.check size={16} />
                </span>
                <span>No commitment required</span>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal animation="fadeRight" className="cta-form-wrapper">
            <motion.form
              className="cta-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="form-title">Request a Demo</h3>

              <div className="form-group">
                <label htmlFor="email">Work Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  placeholder="Your company name"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone (Optional)</label>
                <div className="phone-input-group">
                  <select
                    id="countryCode"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="form-input form-select country-code-select"
                  >
                    {COUNTRY_CODES.map((cc) => (
                      <option key={`${cc.code}-${cc.country}`} value={cc.code}>
                        {cc.code} ({cc.country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input phone-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="fleetSize">Fleet Size (MW)</label>
                <select
                  id="fleetSize"
                  name="fleetSize"
                  value={formData.fleetSize}
                  onChange={handleChange}
                  required
                  className="form-input form-select"
                >
                  <option value="">Select fleet size</option>
                  <option value="1-5">1-5 MW</option>
                  <option value="5-10">5-10 MW</option>
                  <option value="10-50">10-50 MW</option>
                  <option value="50-100">50-100 MW</option>
                  <option value="100+">100+ MW</option>
                </select>
              </div>

              <GlowButton
                type="submit"
                variant="solar"
                size="large"
                className="form-submit"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Sending...' : 'Get Started'}
              </GlowButton>

              {status === 'success' && (
                <p className="form-success">
                  Thank you! We&apos;ll be in touch soon.
                </p>
              )}

              {status === 'error' && (
                <p className="form-error">
                  Something went wrong. Please try again.
                </p>
              )}

              <p className="form-note">
                By submitting, you agree to our Privacy Policy and Terms of Service.
              </p>
            </motion.form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
